import dayjs from 'dayjs';
import {
    BrushType,
    CellState,
    DAYS,
    DaySchedule,
    ExceptionExclude,
    ExceptionInclude,
    TimeSlot,
    WeekSchedule,
} from '../types/workingHours';

const DOW_TO_DAY: Record<number, (typeof DAYS)[number]> = {
    0: '일',
    1: '월',
    2: '화',
    3: '수',
    4: '목',
    5: '금',
    6: '토',
};

export interface DayOfMonth {
    date: string; // YYYY-MM-DD
    day: number; // 1..31
    dayOfWeek: (typeof DAYS)[number];
    isSaturday: boolean;
    isSunday: boolean;
}

/** 해당 월의 모든 날짜를 반환 */
export const getDaysOfMonth = (year: number, month: number): DayOfMonth[] => {
    const first = dayjs(`${year}-${String(month).padStart(2, '0')}-01`);
    const daysInMonth = first.daysInMonth();
    const result: DayOfMonth[] = [];
    for (let d = 1; d <= daysInMonth; d++) {
        const cur = first.date(d);
        const dow = cur.day();
        result.push({
            date: cur.format('YYYY-MM-DD'),
            day: d,
            dayOfWeek: DOW_TO_DAY[dow],
            isSaturday: dow === 6,
            isSunday: dow === 0,
        });
    }
    return result;
};

/** state → 특정 셀의 상태 계산 */
export const computeCellState = (
    date: string,
    dayOfWeek: (typeof DAYS)[number],
    weekSchedule: WeekSchedule,
    includes: ExceptionInclude[],
    excludes: ExceptionExclude[],
): CellState => {
    // 1) exclude가 최우선
    const exc = excludes.find((e) => e.date === date);
    if (exc) return { type: 'exclude', memo: exc.memo };

    // 2) include 오버레이
    const inc = includes.find((i) => i.date === date);
    if (inc) {
        return {
            type: 'include',
            startTime: inc.startTime,
            endTime: inc.endTime,
            memo: inc.memo,
        };
    }

    // 3) 기본 요일 루틴 — enabled이고 시간 구간이 1개 이상 있어야 근무
    const day = weekSchedule[dayOfWeek];
    if (day && day.enabled && day.timeSlots.length > 0) return { type: 'default-on' };
    return { type: 'default-off' };
};

/** 브러시 적용 — 특정 셀들을 지정된 브러시 상태로 변경한 결과를 반환 */
export const applyBrush = (
    brush: BrushType,
    targets: { coachId: string; date: string }[],
    includeMap: Record<string, ExceptionInclude[]>,
    excludeMap: Record<string, ExceptionExclude[]>,
): {
    includeMap: Record<string, ExceptionInclude[]>;
    excludeMap: Record<string, ExceptionExclude[]>;
} => {
    // 깊은 복사 (id/flags 보존)
    const nextInc: Record<string, ExceptionInclude[]> = {};
    const nextExc: Record<string, ExceptionExclude[]> = {};
    Object.keys(includeMap).forEach((k) => (nextInc[k] = [...(includeMap[k] ?? [])]));
    Object.keys(excludeMap).forEach((k) => (nextExc[k] = [...(excludeMap[k] ?? [])]));

    targets.forEach(({ coachId, date }) => {
        if (!nextInc[coachId]) nextInc[coachId] = [];
        if (!nextExc[coachId]) nextExc[coachId] = [];

        // 기존 해당 날짜 항목 제거
        nextInc[coachId] = nextInc[coachId].filter((i) => i.date !== date);
        nextExc[coachId] = nextExc[coachId].filter((e) => e.date !== date);

        if (brush.kind === 'default') {
            // 제거만 하고 종료
            return;
        }
        if (brush.kind === 'exclude') {
            nextExc[coachId].push({
                id: `${coachId}-${date}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                date,
                memo: brush.memo,
                isSaved: true,
                isMemoEditing: false,
            });
            return;
        }
        if (brush.kind === 'include') {
            // 시간이 없으면 저장 불가 (isSaved=false) — 드래그 종료 시 popover로 보완
            const startTime = brush.startTime ?? '';
            const endTime = brush.endTime ?? '';
            nextInc[coachId].push({
                id: `${coachId}-${date}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                date,
                startTime,
                endTime,
                memo: brush.memo,
                isSaved: !!startTime && !!endTime,
                isMemoEditing: false,
            });
        }
    });

    return { includeMap: nextInc, excludeMap: nextExc };
};

/** 특정 셀의 include 항목 업데이트 (시간/메모) */
export const updateIncludeAt = (
    coachId: string,
    date: string,
    patch: Partial<Pick<ExceptionInclude, 'startTime' | 'endTime' | 'memo' | 'isSaved'>>,
    includeMap: Record<string, ExceptionInclude[]>,
): Record<string, ExceptionInclude[]> => {
    const list = includeMap[coachId] ?? [];
    const next = list.map((i) => {
        if (i.date !== date) return i;
        const merged = { ...i, ...patch };
        merged.isSaved = !!merged.startTime && !!merged.endTime;
        return merged;
    });
    return { ...includeMap, [coachId]: next };
};

/** 특정 셀의 exclude 메모 업데이트 */
export const updateExcludeAt = (
    coachId: string,
    date: string,
    patch: Partial<Pick<ExceptionExclude, 'memo'>>,
    excludeMap: Record<string, ExceptionExclude[]>,
): Record<string, ExceptionExclude[]> => {
    const list = excludeMap[coachId] ?? [];
    const next = list.map((e) => (e.date === date ? { ...e, ...patch } : e));
    return { ...excludeMap, [coachId]: next };
};

/**
 * 강사의 enabled 요일 공통 시간 반환:
 * - 모든 enabled 요일이 동일한 단일 슬롯({start, end})을 가지면 그 값
 * - enabled 요일이 없으면 null
 * - 그 외 (요일별 슬롯이 다르거나, 슬롯 수가 다르거나, 다중 슬롯이 있으면) 'mixed'
 */
export const getCommonWorkTime = (
    schedule: WeekSchedule,
): { startTime: string; endTime: string } | 'mixed' | null => {
    const enabled = DAYS.map((d) => schedule[d]).filter((s) => s.enabled);
    if (enabled.length === 0) return null;
    // 다중 슬롯이 하나라도 있으면 mixed
    if (enabled.some((s) => s.timeSlots.length !== 1)) return 'mixed';
    const first = enabled[0].timeSlots[0];
    if (!first) return 'mixed';
    const allSame = enabled.every((s) => {
        const slot = s.timeSlots[0];
        return slot && slot.startTime === first.startTime && slot.endTime === first.endTime;
    });
    if (!allSame) return 'mixed';
    return { startTime: first.startTime, endTime: first.endTime };
};

/**
 * 시간 일괄 적용 — 모든 요일을 enabled=true로 만들고 단일 슬롯으로 덮어쓰기.
 * 비활성 요일도 자동으로 켜진다 ("일괄"은 전체 적용).
 */
export const setCommonWorkTime = (
    schedule: WeekSchedule,
    startTime: string,
    endTime: string,
): WeekSchedule => {
    const next = { ...schedule };
    DAYS.forEach((d) => {
        next[d] = {
            enabled: true,
            timeSlots: [{ startTime, endTime }],
        };
    });
    return next;
};

// ============================================================
// 검증 — 자정 넘김 차단 + 슬롯 겹침 차단
// ============================================================

/**
 * ISO를 분(minutes)으로 변환.
 * - isEnd=true 이고 ISO가 자정(00:00 다음날 또는 0:0)이면 1440 으로 정규화.
 */
const isoToMinutes = (iso: string, isEnd: boolean): number | null => {
    if (!iso) return null;
    const d = dayjs(iso);
    if (!d.isValid()) return null;
    const h = d.hour();
    const m = d.minute();
    if (isEnd && h === 0 && m === 0) return 1440;
    return h * 60 + m;
};

export type SlotErrorKind = 'empty' | 'invalid-range' | 'overlap';

export interface SlotError {
    slotIndex: number;
    kind: SlotErrorKind;
    /** 사용자에게 보여줄 메시지 */
    message: string;
}

/**
 * 단일 요일 스케줄 검증 — 슬롯별 에러 배열을 반환.
 * - 빈 시간 (`empty`): 시작/종료 중 하나라도 미입력
 * - 잘못된 구간 (`invalid-range`): start >= end (자정 넘김 포함, end='00:00'은 1440)
 * - 겹침 (`overlap`): 다른 슬롯과 시간이 겹침 (접점 OK)
 *
 * disabled 요일은 항상 빈 배열을 반환.
 */
export const validateDaySchedule = (day: DaySchedule): SlotError[] => {
    if (!day.enabled || day.timeSlots.length === 0) return [];
    const errors: SlotError[] = [];

    // 슬롯별 빈/잘못된 구간 검사
    const intervals: Array<{ slotIndex: number; start: number; end: number } | null> = day.timeSlots.map(
        (slot, i) => {
            if (!slot.startTime || !slot.endTime) {
                errors.push({ slotIndex: i, kind: 'empty', message: '시간을 입력해 주세요' });
                return null;
            }
            const start = isoToMinutes(slot.startTime, false);
            const end = isoToMinutes(slot.endTime, true);
            if (start === null || end === null) {
                errors.push({ slotIndex: i, kind: 'empty', message: '시간을 입력해 주세요' });
                return null;
            }
            if (start >= end) {
                errors.push({
                    slotIndex: i,
                    kind: 'invalid-range',
                    message: '시작 시간이 종료 시간보다 빨라야 해요',
                });
                return null;
            }
            return { slotIndex: i, start, end };
        },
    );

    // 겹침 검사 (유효한 슬롯끼리만)
    const valid = intervals.filter(
        (iv): iv is { slotIndex: number; start: number; end: number } => iv !== null,
    );
    // 시작 시간 오름차순으로 정렬한 뒤 인접 검사 (접점 OK: prev.end == cur.start 허용)
    const sorted = [...valid].sort((a, b) => a.start - b.start);
    for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].start < sorted[i - 1].end) {
            // 두 슬롯 모두 에러로 표시
            errors.push({
                slotIndex: sorted[i].slotIndex,
                kind: 'overlap',
                message: '다른 시간 구간과 겹쳐요',
            });
            errors.push({
                slotIndex: sorted[i - 1].slotIndex,
                kind: 'overlap',
                message: '다른 시간 구간과 겹쳐요',
            });
        }
    }

    return errors;
};

/** 슬롯 단건이 valid한지(시간 채워졌고 start<end) 빠른 확인 */
export const isSlotTimeValid = (slot: TimeSlot): boolean => {
    if (!slot.startTime || !slot.endTime) return false;
    const start = isoToMinutes(slot.startTime, false);
    const end = isoToMinutes(slot.endTime, true);
    if (start === null || end === null) return false;
    return start < end;
};

/** 강사 한 명의 전체 주간 스케줄에 에러가 있는지 */
export const hasInvalidSchedule = (schedule: WeekSchedule): boolean => {
    return DAYS.some((d) => validateDaySchedule(schedule[d]).length > 0);
};
